import React, { useCallback, useEffect, useRef, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';

import { getSignInErrorMessage } from 'app/auth/authUtils';
import {
  parseAutoLoginTokensFromSearch,
  shouldPromptPasskeyEnrollFromSearch,
} from 'app/pages/login/AutoLogin.utils';
import accountsJwtService from 'app/services/jwtService';
import { sessionStorage } from 'app/services/storage';
import SplashScreen from 'app/shared/components/common/SplashScreen';
import PropTypes from 'prop-types';

import { PASSKEY_ENROLLMENT_PROMPT_FLAG } from 'constants/auth';
import { PATH_AUTO_LOGIN } from 'constants/authPaths';
import { useEffectSafeStrictMode } from 'hooks/useEffectSafeStrictMode';
import { logger } from 'utils/logger';

import {
  logoutMasterAction,
  setMasterDataAction,
  setTenantBrandingAction,
  setTenantDomainTypeAction,
  setTenantNameAction,
} from 'store/auth/actions';
import { getMasterRole } from 'store/auth/selectors';
import { showErrorMessageAction, showMessageAction } from 'store/global/actions';

/* DEV-3807: on the TPO->AdminPanel cert handoff, Authentication.init() runs (and fires the first
   authenticated authorization-context request) before AutoLogin mounts and seeds the URL-param token.
   That first request used to 401 against a stale host session, triggering a refresh that surfaced the
   spurious "RefreshToken is expired" + "Unknown error" banners before the silent retry recovered.
   Seeding the handoff token into storage synchronously here — before init() reads it — makes that first
   request carry a valid token, so the banner never shows. No-ops for every non-handoff load. */
const seedAutoLoginTokenIfHandoff = () => {
  if (window.location.pathname !== PATH_AUTO_LOGIN) {
    return;
  }

  const autoLoginTokens = parseAutoLoginTokensFromSearch(window.location.search);

  if (!autoLoginTokens) {
    return;
  }

  // DEV-4018: arm the one-time enroll-on-first-login prompt (DEV-3881) SYNCHRONOUSLY, before init() fires
  // onAutoLogin and the authed shell (and its PasskeyEnrollmentHost, which consumes the flag once on mount)
  // renders. The AutoLogin page component sets the flag too late — inside its post-sign-in .then(), which
  // runs after the host has already mounted and read an absent flag — so the prompt would never open.
  if (shouldPromptPasskeyEnrollFromSearch(window.location.search)) {
    sessionStorage.setItem(PASSKEY_ENROLLMENT_PROMPT_FLAG, true);
  }

  try {
    accountsJwtService.persistAccessToken(
      autoLoginTokens.token,
      autoLoginTokens.refreshToken,
      autoLoginTokens.refreshTokenExpiresAt,
    );
  } catch {
    /* A malformed handoff token falls through to the normal init() flow, which surfaces the real error. */
  }
};

const Authentication = ({ children, isFeatureFlagsLoaded = false }) => {
  const dispatch = useDispatch();

  const masterRole = useSelector(getMasterRole);

  const [isAuthCheckCompleted, setIsAuthCheckCompleted] = useState(false);

  const masterRoleRef = useRef(masterRole);

  const isDataLoading = !isAuthCheckCompleted || !isFeatureFlagsLoaded;

  useEffect(() => {
    masterRoleRef.current = masterRole;
  }, [masterRole]);

  const onJwtCheck = useCallback(
    async () =>
      new Promise((res, rej) => {
        seedAutoLoginTokenIfHandoff();

        accountsJwtService.on('onAutoLogin', () => {
          /**
           * Sign in and retrieve master data from Api
           */
          accountsJwtService
            .signInWithToken()
            .then(master => {
              dispatch(setMasterDataAction(master));
              res();
            })
            .catch(error => {
              dispatch(
                showMessageAction({
                  message: getSignInErrorMessage(error),
                  variant: 'error',
                }),
              );
              rej();
            });
        });

        accountsJwtService.on('onAutoLogout', (message, err) => {
          if (masterRoleRef.current) {
            dispatch(logoutMasterAction());
          }

          if (message) {
            dispatch(showMessageAction({ message, err }));
          }

          rej();
        });

        accountsJwtService.on('onNoToken', () => {
          rej('onNoToken');
        });

        accountsJwtService.on('onTenantLoaded', ({ domainType, name, branding }) => {
          dispatch(setTenantDomainTypeAction(domainType));
          dispatch(setTenantNameAction(name));
          dispatch(setTenantBrandingAction(branding));
        });

        accountsJwtService.init().catch(err => {
          dispatch(
            showErrorMessageAction({
              message: err?.response?.data?.error?.message || 'Unknown error',
              err,
            }),
          );
          rej('initError');
        });

        return Promise.resolve();
      }),
    [dispatch],
  );

  useEffectSafeStrictMode(() => {
    onJwtCheck()
      .catch(err => {
        if (err === 'initError') {
          return logger.error(err);
        }

        logger.info(err);
      })
      .finally(() => setIsAuthCheckCompleted(true));
  }, [onJwtCheck]);

  return isDataLoading ? <SplashScreen /> : children;
};

Authentication.propTypes = {
  isFeatureFlagsLoaded: PropTypes.bool,
};

export default Authentication;
